import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { createFormHelper } from "lib/Form";
import { APPOINTMENT_NOTE_TYPES } from "models";
import "modules/AppointmentDetails/components/AppointmentNoteModal/AppointmentNoteModal.scss";
import { AppointmentNoteForm, AppointmentNoteModalHeader } from "modules/AppointmentDetails/components/AppointmentNoteModal/components";
import {
  generateAddAppointmentNoteRequest,
  generateUpdateAppointmentNoteRequest,
  useAddAppointmentNoteMutation,
  useDeleteAppointmentNoteMutation,
  useUpdateAppointmentNoteMutation
} from "modules/AppointmentDetails/components/AppointmentNoteModal/hooks";
import { useAppointmentNotePhoneOptions } from "modules/AppointmentDetails/components/AppointmentNotes/hooks";
import { toISOString } from "utils/dateHelperFunctions";

import type { Appointment, AppointmentNote } from "models";
import type { AppointmentNoteFormValues } from "modules/AppointmentDetails/components/AppointmentNoteModal/components";

type AppointmentNoteModalProps = {
  appointment: Appointment;
  createAppointmentNoteType?: APPOINTMENT_NOTE_TYPES | null;
  selectedAppointmentNote: AppointmentNote | null;
  onUnselectNote: () => void;
  onSave?: (createAppointmentNoteType: APPOINTMENT_NOTE_TYPES, id: number) => void;
};

const FormHelper = createFormHelper<AppointmentNoteFormValues>();

export const AppointmentNoteModal = ({ appointment, selectedAppointmentNote, createAppointmentNoteType, onUnselectNote, onSave }: AppointmentNoteModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { phoneOptions } = useAppointmentNotePhoneOptions(appointment);
  const { mutateAsync: addAppointmentNote, isPending: isAddAppointmentNotePending } = useAddAppointmentNoteMutation();
  const { mutateAsync: deleteAppointmentNote, isPending: isDeleteAppointmentNotePending } = useDeleteAppointmentNoteMutation();
  const { mutateAsync: updateAppointmentNote, isPending: isUpdateAppointmentNotePending } = useUpdateAppointmentNoteMutation();
  const contact = phoneOptions.find(item => item.text === selectedAppointmentNote?.phone_nr);

  if (!createAppointmentNoteType && !selectedAppointmentNote) return;

  const noteType = createAppointmentNoteType || selectedAppointmentNote?.appointment_note_type_id;
  if (!noteType) return;

  const handleCloseModal = () => {
    onUnselectNote();
    setHasErrors(false);
  };

  const isAppointmentNoteFormValid = (noteType: APPOINTMENT_NOTE_TYPES, { note, phone_nr, attachments }: AppointmentNoteFormValues) => {
    switch (noteType) {
      case APPOINTMENT_NOTE_TYPES.Info:
      case APPOINTMENT_NOTE_TYPES.WO:
      case APPOINTMENT_NOTE_TYPES.Main:
      case APPOINTMENT_NOTE_TYPES.RecurringCar:
      case APPOINTMENT_NOTE_TYPES.BackOrder:
      case APPOINTMENT_NOTE_TYPES.MakeAnOffer:
      case APPOINTMENT_NOTE_TYPES.ParkingLocation:
      case APPOINTMENT_NOTE_TYPES.NoDrive:
        return note.length > 0;
      case APPOINTMENT_NOTE_TYPES.CallCustomer:
      case APPOINTMENT_NOTE_TYPES.TempDriver:
        return note.length > 0 && phone_nr.length > 0;
      case APPOINTMENT_NOTE_TYPES.Bill:
      case APPOINTMENT_NOTE_TYPES.Attachment:
        return attachments !== undefined && attachments.length > 0;
      default:
        return true;
    }
  };

  const validateForm = (values: AppointmentNoteFormValues) => {
    const isValid = isAppointmentNoteFormValid(noteType, values);
    setHasErrors(!isValid);
    return isValid;
  };

  const handleDeleteNote = async () => {
    if (selectedAppointmentNote) await deleteAppointmentNote({ appointment_note_id: selectedAppointmentNote.id });

    setShowDeleteConfirmation(false);
    handleCloseModal();
  };

  const handleSubmit = async (values: AppointmentNoteFormValues) => {
    if (!validateForm(values)) return;

    const { note, attachments, backorder_date, visible_to_mechanic, phone_nr, email, name } = values;
    const fields = { appointment_id: appointment.id, note, attachments, backorder_date, visible_to_mechanic, phone_nr, email, name };

    if (selectedAppointmentNote) {
      const request = generateUpdateAppointmentNoteRequest(noteType, selectedAppointmentNote.id, fields);
      if (!request) return;

      await updateAppointmentNote({ appointment_note_id: selectedAppointmentNote.id, fields, request });
    } else {
      const request = generateAddAppointmentNoteRequest({ fields, type: noteType });
      if (!request) return;

      const appointmentNoteId = await addAppointmentNote({ type: noteType, fields, request });
      onSave?.(noteType, appointmentNoteId);
    }

    handleCloseModal();
  };

  return (
    <>
      <Modal open={Boolean(noteType)}>
        <Modal.Header>
          <AppointmentNoteModalHeader type={noteType} isEdit={Boolean(selectedAppointmentNote)} />
        </Modal.Header>

        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            appointment_note_id: selectedAppointmentNote?.id || null,
            appointment_id: appointment.id,
            backorder_date: selectedAppointmentNote?.backorder_date || toISOString(selectedAppointmentNote?.backorder_date),
            name: selectedAppointmentNote?.name || "",
            note: selectedAppointmentNote?.note || "",
            phone_nr: selectedAppointmentNote?.phone_nr || "",
            visible_to_mechanic: selectedAppointmentNote?.visible_to_mechanic || false,
            email: selectedAppointmentNote?.email || "",
            note_suggestion_id: null,
            phone_nr_id: contact ? contact.value : "other",
            attachments: selectedAppointmentNote?.attachments
          }}
        >
          <div className="AddUpdateAppointmentNote">
            <AppointmentNoteForm
              hasValidationErrors={hasErrors}
              onClose={handleCloseModal}
              onSubmit={handleSubmit}
              noteType={noteType}
              appointment={appointment}
              appointmentNote={selectedAppointmentNote}
              isAddUpdateNotePending={isUpdateAppointmentNotePending || isAddAppointmentNotePending}
              isDeleteNonePending={isDeleteAppointmentNotePending}
              openCustomConfirmPopup={() => setShowDeleteConfirmation(true)}
            />
          </div>
        </FormHelper.Form>
      </Modal>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteConfirmation}
        handleCancel={() => setShowDeleteConfirmation(false)}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteNote}
        isLoading={isDeleteAppointmentNotePending}
      />
    </>
  );
};
